type TokenHeaders = Record<string, string>;

type FetchJsonError = Error & {
  status?: number;
  body?: unknown;
};

export type FetchJsonOptions = RequestInit & {
  headers?: HeadersInit;
};

export function fetchJson(
  path: string,
  token?: string | TokenHeaders | null,
  options: FetchJsonOptions = {},
) {
  const { signal: externalSignal, headers: providedHeaders = {}, ...rest } = options || {};
  const controller = externalSignal ? null : new AbortController();
  const signal = externalSignal ?? controller!.signal;

  const supportsHeaders = typeof Headers === 'function';
  const headers: Headers | TokenHeaders = supportsHeaders ? new Headers() : {};

  const setHeader = (key: string, value?: string) => {
    if (typeof value === 'undefined') return;
    if (supportsHeaders && headers instanceof Headers) {
      headers.set(key, value);
    } else {
      (headers as TokenHeaders)[key] = value;
    }
  };

  setHeader('accept', 'application/json');

  if (providedHeaders instanceof Headers) {
    providedHeaders.forEach((value, key) => {
      setHeader(key, value);
    });
  } else if (providedHeaders && typeof providedHeaders === 'object') {
    Object.entries(providedHeaders).forEach(([key, value]) => {
      if (typeof value === 'string') {
        setHeader(key, value);
      }
    });
  }

  if (token) {
    if (typeof token === 'string') {
      const value = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      setHeader('Authorization', value);
    } else if (typeof token === 'object') {
      Object.entries(token).forEach(([key, value]) => {
        setHeader(key, value);
      });
    }
  }

  const promise = fetch(path, {
    ...rest,
    headers,
    signal,
  }).then(async (response) => {
    const contentType = response.headers.get('content-type') || '';
    let payload: unknown = null;

    if (contentType.includes('application/json')) {
      payload = await response.json().catch(() => null);
    } else {
      const text = await response.text().catch(() => '');
      payload = text;
    }

    if (!response.ok) {
      let message = response.statusText || 'Request failed';
      if (payload && typeof payload === 'object') {
        const typedPayload = payload as { error?: string; message?: string };
        const errorMessage = typedPayload.error || typedPayload.message;
        if (typeof errorMessage === 'string' && errorMessage.trim()) {
          message = errorMessage;
        }
      }
      const error: FetchJsonError = new Error(message);
      error.status = response.status;
      error.body = payload;
      throw error;
    }

    return payload;
  });

  const abort = () => {
    if (controller) {
      controller.abort();
    }
  };

  return { promise, abort, controller, signal };
}
